/**
 * The document head of the open page (SEO, ADR-0048, ADR-0061, ADR-0062). A public page arrives prerendered with its own
 * title, description, canonical, hreflang alternates and Open Graph tags in its language; on client-side navigation the
 * app writes the same values from the same table (app/publicRoutes.ts), so the head always matches the page. Any other
 * path gets the brand title and `noindex`.
 */
import { DEFAULT_LANG, LANGS, type Lang } from "@prop/i18n";
import { BRAND, HREFLANG, OG_LOCALE, pageMeta } from "../app/publicRoutes";

function setMeta(attr: "name" | "property", key: string, content: string | null): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (content === null) return void el?.remove();
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.append(el);
  }
  el.content = content;
}

function setCanonical(href: string | null): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (href === null) return void el?.remove();
  if (!el) {
    el = document.createElement("link");
    el.rel = "canonical";
    document.head.append(el);
  }
  el.href = href;
}

/** The hreflang alternates of a public page (ADR-0062), or none. */
function setAlternates(alternates: Record<Lang, string> | null): void {
  for (const el of document.head.querySelectorAll('link[rel="alternate"][hreflang]')) el.remove();
  if (!alternates) return;
  const links = [...LANGS.map((l) => [HREFLANG[l], alternates[l]] as const), ["x-default", alternates[DEFAULT_LANG]] as const];
  for (const [hreflang, href] of links) {
    const el = document.createElement("link");
    el.rel = "alternate";
    el.hreflang = hreflang;
    el.href = href;
    document.head.append(el);
  }
}

/** Writes the head of `path` (without its prefix) in `lang`: its public meta, or the brand title with noindex. */
export function applyPageMeta(path: string, lang: Lang): void {
  const m = pageMeta(path, lang);
  document.title = m?.title ?? BRAND;
  setMeta("name", "robots", m ? null : "noindex");
  setCanonical(m?.canonical ?? null);
  setAlternates(m?.alternates ?? null);
  if (!m) return;
  setMeta("name", "description", m.description);
  setMeta("property", "og:url", m.canonical);
  setMeta("property", "og:locale", OG_LOCALE[lang]);
  setMeta("property", "og:title", m.ogTitle ?? m.title);
  setMeta("property", "og:description", m.ogDescription ?? m.description);
  setMeta("name", "twitter:title", m.ogTitle ?? m.title);
  setMeta("name", "twitter:description", m.ogDescription ?? m.description);
}
